"use client";

import dynamic from "next/dynamic";
import { ScoreBar } from "@/components/shared/score-bar";

const RadarChart = dynamic(
  () =>
    import("@/components/shared/radar-chart").then((mod) => mod.RadarChart),
  { ssr: false }
);

type BreakdownItem = {
  label: string;
  score: number;
  reasoning: string;
};

type FundScoreBreakdownProps = {
  overall: number;
  breakdown: Record<string, BreakdownItem>;
};

function scoreColor(score: number): string {
  if (score >= 8) return "from-cgreen to-cgreen/70";
  if (score >= 6) return "from-blue to-blue-glow";
  if (score >= 4) return "from-gold to-gold/70";
  return "from-cred to-cred/70";
}

export function FundScoreBreakdown({
  overall,
  breakdown,
}: FundScoreBreakdownProps) {
  const entries = Object.entries(breakdown);

  // Prepare radar chart axes
  const radarAxes = entries.map(([, item]) => ({
    label: item.label,
    value: item.score,
  }));

  return (
    <div className="space-y-6">
      {/* Overall score header */}
      <div className="flex items-center gap-4">
        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-navy-mid border border-surface-light/30">
          <span className="text-foreground text-2xl font-bold font-tabular">
            {overall.toFixed(1)}
          </span>
        </div>
        <div>
          <span className="text-foreground text-sm font-serif">
            Overall WTM Fund Score
          </span>
          <span className="text-dim text-xs block mt-0.5">
            Based on {entries.length} evaluation dimensions
          </span>
        </div>
      </div>

      {/* Radar chart */}
      {radarAxes.length >= 3 && (
        <div className="bg-navy-mid/50 rounded-xl p-4">
          <RadarChart axes={radarAxes} />
        </div>
      )}

      {/* Score bars + reasoning */}
      <div className="space-y-4">
        {entries.map(([key, item]) => (
          <div key={key}>
            <ScoreBar
              label={item.label}
              value={item.score}
              max={10}
              color={scoreColor(item.score)}
            />
            {item.reasoning && (
              <p className="text-dim text-xs leading-relaxed mt-1 ml-0.5">
                {item.reasoning}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
